import { AnyAction } from '@reduxjs/toolkit';

import { State } from '../types/state.interface';
import { Mandate } from '../interfaces/client/mandate.interface';

import { MandateActions } from '../actions/mandate/mandate.actions';

/**
 * paper_trail version entry as returned by GET /mandates/:id/versions.
 * `changes` is an array of field-level diffs; for a fresh `create` event
 * `changes` may be empty.  `actor` is the user's full_name resolved from
 * `whodunnit` server-side; null for system-attributed changes.
 */
export interface MandateVersion {
  id: number;
  event: 'create' | 'update' | 'destroy';
  created_at: string;
  actor_id: string | null;
  actor: string | null;
  changes: Array<{
    field: string;
    from: unknown;
    to: unknown;
  }>;
}

/** Active Storage attachment on a Mandate, as serialised by the v3 docs jbuilder. */
export interface MandateDocument {
  id: number;
  blob_id: number;
  name: string;
  content_type: string;
  byte_size: number;
  created_at: string;
  url: string;
}

export interface MandateState extends State {
  mandates: Mandate[];
  mandate?: Mandate;
  // Version history is keyed by mandate id (stringified) so the Activity
  // tab can read it without refetching on every render and so multiple
  // detail-page visits in a session don't dispatch duplicate calls.
  versionsByMandate?: Record<string, MandateVersion[]>;
  // Attachments keyed by mandate id — populated when the Documents tab
  // is opened (or the Recent documents card on Overview is rendered).
  documentsByMandate?: Record<string, MandateDocument[]>;
}

export const initialState: MandateState = {
  mandates: [],
  versionsByMandate: {},
  documentsByMandate: {}
};

export const mandateReducer = (state: MandateState = initialState, action: AnyAction) => {
  switch (action.type) {
    case MandateActions.SET_MANDATES:
      const { mandates } = action;
      return {
        ...state,
        mandates
      };
    case MandateActions.SET_MANDATE:
      const { mandate } = action;
      return {
        ...state,
        mandate
      };
    case MandateActions.SET_MANDATE_VERSIONS: {
      const { id, versions } = action as { id: number | string; versions: MandateVersion[] };
      return {
        ...state,
        versionsByMandate: {
          ...(state.versionsByMandate || {}),
          [String(id)]: versions
        }
      };
    }
    case MandateActions.SET_MANDATE_DOCUMENTS: {
      const { id, documents } = action as {
        id: number | string;
        documents: MandateDocument[];
      };
      return {
        ...state,
        documentsByMandate: {
          ...(state.documentsByMandate || {}),
          [String(id)]: documents
        }
      };
    }
    default:
      return state;
  }
};
